// frontend/src/features/livestock/ComparePageBody.tsx
// The compare page for any subject's photos
// (docs/plans/2026-10-29-livestock-compare-design.md, 8.2), moved from
// TankShotsComparePage, which is now a thin wrapper: a slider or side by side,
// full screen, and a picker per side (presets, filmstrip, by date). The URL
// always names the two photos shown (replaced, not pushed, on each change);
// without ids it is First vs Latest, and an id the subject doesn't have falls
// back to First or Latest with a notice. The handle position is held here, so
// the page and full screen share it; a new pair starts again from 50%.
// Lined-up photos show framed, and each side has Line up, which saves to the
// photo (docs/plans/2026-10-29-tank-shot-framing-design.md, 7.3).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Container,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconInfoCircle,
  IconLayoutColumns,
  IconMaximize,
  IconFocusCentered,
  IconShare,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/common/EmptyState';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRefetchOnImageError } from '@/hooks/usePhotoInbox';
import { usePhotoSubject, useSubjectPhoto } from '@/hooks/usePhotoSubject';
import type { ComparePhoto, PhotoIndexItem } from '@/types/photos';
import type { CompareShotImage } from './CompareImage';
import { COMPARE_MODES, CompareFullScreen, CompareView, type CompareMode } from './CompareView';
import { CompareShotPicker, type PickerChip } from './CompareShotPicker';
import {
  COMPARE_PRESETS,
  chooseShot,
  compareReason,
  gapLabel,
  missingNotice,
  nearestShot,
  parseShotId,
  resolvePairDetail,
  resolvePreset,
  type CompareSide,
  type ComparePair,
} from './compareShots';
import { NO_ZOOM, type CompareZoom } from './compareZoom';
import { formatPhotoDay } from './photoInboxFormat';
import { PhotoLineUpModal } from './LineUpModal';
import { lineUpMode } from './lineUpReference';
import type { PhotoSubject } from './photoSubject';
import { ShareCompareModal, type ShareOrganism } from './ShareCompareModal';
import classes from './Compare.module.css';

/** How a side's photo was chosen: lights its chip and says "Nearest shot: ...". */
type PickSource = { kind: 'preset'; key: string } | { kind: 'date' } | { kind: 'pick' };

interface SideNotice {
  side: CompareSide;
  text: string;
}

/** The images for one side: index thumb at once, the photo's bigger variants once fetched. */
function sideImage(
  item: PhotoIndexItem,
  shot: ComparePhoto | undefined,
  fullScreen: boolean
): CompareShotImage {
  const display = shot?.photo_display_url ?? null;
  return {
    src: fullScreen ? (shot?.photo_url ?? display) : display,
    placeholder: fullScreen ? (display ?? item.thumb_url) : item.thumb_url,
    date: item.entry_date,
    // The shot's own read is the freshest once fetched (a Line up refreshes both)
    framing: shot ? (shot.framing ?? null) : (item.framing ?? null),
  };
}

/** The browser router's index of this entry in the app's history (0: the first), if known. */
function readHistoryIndex(): number | null {
  const state: unknown = typeof window === 'undefined' ? null : window.history.state;
  const idx = state && typeof state === 'object' ? (state as { idx?: unknown }).idx : undefined;
  return typeof idx === 'number' ? idx : null;
}

export interface ComparePageBodyProps {
  /** Undefined while the subject is still being worked out (a skeleton shows). */
  subject: PhotoSubject | undefined;
  /**
   * 'link' (Tank Shots: Back is a link to backTo) or 'history' (Back goes back,
   * or replaces to backTo when the page was opened straight from a link).
   */
  backMode: 'link' | 'history';
  /** Under the "Compare" heading: the tank's or the organism's name. */
  subtitle: string;
  backTo: string;
  /** "Back to Tank Shots" */
  backLabel: string;
  /** The subject in the URL is still being selected (useTankParam's result): wait. */
  switching: boolean;
  /**
   * Changes when the user switches subject while the page is open (the tank
   * picker): ids from before then fall back without a "no longer here" notice.
   */
  switchKey: string | null;
  /** Under the empty state's title. */
  emptyDescription: string;
  /** For the share modal: the name a viewer can be shown. */
  shareName: string;
  /** For the share modal, on an organism's page: its names and type. */
  shareOrganism?: ShareOrganism;
}

export function ComparePageBody({
  subject,
  backMode,
  subtitle,
  backTo,
  backLabel,
  switching,
  switchKey,
  emptyDescription,
  shareName,
  shareOrganism,
}: ComparePageBodyProps): JSX.Element {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  // Waiting for the subject: being selected, or not known yet.
  const waiting = switching || !subject;
  const [params, setParams] = useSearchParams();
  const api = usePhotoSubject(subject);
  const { index, words } = api;
  const items = useMemo(() => index.data?.items ?? [], [index.data]);

  const urlBefore = parseShotId(params.get('before'));
  const urlAfter = parseShotId(params.get('after'));
  const resolved = useMemo(
    () => resolvePairDetail(items, urlBefore, urlAfter),
    [items, urlBefore, urlAfter]
  );
  const pair = resolved?.pair ?? null;

  const [mode, setMode] = useState<CompareMode>('slider');
  const [fullOpen, setFullOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  /** The side whose shot is being lined up. */
  const [liningUp, setLiningUp] = useState<CompareSide | null>(null);
  // Mantine's Modal leaves the page under it in the accessibility tree, so the
  // page's viewer is taken out while full screen shows (no second slider for a
  // screen reader, no second set of images). Its height is held meanwhile, so
  // the page doesn't shrink under the modal and lose its scroll position.
  const pageViewerRef = useRef<HTMLDivElement>(null);
  const [heldHeight, setHeldHeight] = useState(0);
  const openFullScreen = (): void => {
    setHeldHeight(pageViewerRef.current?.offsetHeight ?? 0);
    setFullOpen(true);
  };
  const [sources, setSources] = useState<Record<CompareSide, PickSource | null>>({
    before: null,
    after: null,
  });
  const [notice, setNotice] = useState<SideNotice | null>(null);
  /** "That shot is no longer here, showing First": the URL named a photo the subject doesn't have. */
  const [goneNotice, setGoneNotice] = useState<string | null>(null);

  // The slider's handle, shared by the page and full screen. A new pair starts from the middle.
  const [position, setPosition] = useState(50);
  // The synced zoom, shared by the page and full screen too; a new pair starts unzoomed.
  const [zoom, setZoom] = useState<CompareZoom>(NO_ZOOM);
  const pairKey = pair ? `${pair.beforeId}-${pair.afterId}` : '';
  const [positionFor, setPositionFor] = useState(pairKey);
  if (positionFor !== pairKey) {
    setPositionFor(pairKey);
    setPosition(50);
    setZoom(NO_ZOOM);
  }

  // A switch in the tank picker keeps the old tank's ids in the URL for a moment:
  // those fall back to First vs Latest without saying a shot has gone.
  const seenKey = useRef(switchKey);
  const staleIds = useRef(false);
  useEffect(() => {
    if (seenKey.current && switchKey && seenKey.current !== switchKey) {
      staleIds.current = true;
      setGoneNotice(null);
    }
    if (switchKey) seenKey.current = switchKey;
  }, [switchKey]);

  const writePair = useCallback(
    (next: ComparePair): void => {
      const search = new URLSearchParams(params);
      search.set('before', String(next.beforeId));
      search.set('after', String(next.afterId));
      setParams(search, { replace: true });
    },
    [params, setParams]
  );

  // The URL follows the photos shown: filled in when it had none, corrected when
  // it named a photo the subject doesn't have (deleted, or gone in a refetch of
  // the index: said so), the same photo twice, or After older.
  useEffect(() => {
    if (!resolved || waiting || !switchKey) return;
    const { pair: shown, missing } = resolved;
    if (shown.beforeId === urlBefore && shown.afterId === urlAfter) return;
    if (!staleIds.current && missing.length > 0) setGoneNotice(missingNotice(missing, words));
    staleIds.current = false;
    writePair(shown);
  }, [resolved, urlBefore, urlAfter, switchKey, waiting, writePair, words]);

  const beforeItem = pair ? items.find((item) => item.id === pair.beforeId) : undefined;
  const afterItem = pair ? items.find((item) => item.id === pair.afterId) : undefined;
  const beforeShot = useSubjectPhoto(subject, pair?.beforeId);
  const afterShot = useSubjectPhoto(subject, pair?.afterId);

  const { refetch: refetchIndex } = index;
  const { refetch: refetchBefore } = beforeShot;
  const { refetch: refetchAfter } = afterShot;
  const refetchAll = useCallback(
    () => Promise.all([refetchIndex(), refetchBefore(), refetchAfter()]),
    [refetchIndex, refetchBefore, refetchAfter]
  );
  const onImageError = useRefetchOnImageError(refetchAll);

  const choose = (side: CompareSide, id: number, source: PickSource): void => {
    if (!pair) return;
    const result = chooseShot(items, pair, side, id);
    setGoneNotice(null);
    if (result.outcome === 'same') {
      setNotice({ side, text: `Same ${words.noun}, pick another` });
      return;
    }
    if (result.outcome === 'swapped') {
      setNotice({
        side,
        text:
          side === 'after'
            ? `That ${words.noun} is older, so it is now Before`
            : `That ${words.noun} is newer, so it is now After`,
      });
      setSources({ before: { kind: 'pick' }, after: { kind: 'pick' } });
    } else {
      setNotice(null);
      setSources((current) => ({ ...current, [side]: source }));
    }
    writePair(result.pair);
  };

  const chooseDate = (side: CompareSide, day: string): void => {
    if (!pair) return;
    // The other side's photo is left out, so a date always picks something.
    const otherId = side === 'before' ? pair.afterId : pair.beforeId;
    const candidates = items.filter((item) => item.id !== otherId);
    const nearest = nearestShot(candidates, dayjs(day).hour(12));
    if (nearest) choose(side, nearest.id, { kind: 'date' });
  };

  const reference = api.reference;

  // 'history': back to the page this came from, or (opened straight from a
  // link, nothing to go back to) to backTo in place of this entry. Worked out
  // once, on arrival: the page's own replaces (the ids it writes, the tank it
  // selects) give the entry a new key without adding one to go back to.
  const [openedDirectly] = useState(() => location.key === 'default' || readHistoryIndex() === 0);
  const goBack = (): void => {
    if (openedDirectly) navigate(backTo, { replace: true });
    else navigate(-1);
  };
  const loading = index.isLoading || waiting;
  const notEnough = index.isSuccess && !waiting && items.length < 2;
  const ready = !!pair && !!beforeItem && !!afterItem && !waiting;

  const chipsFor = (side: CompareSide): PickerChip[] => {
    if (!pair) return [];
    const selectedId = side === 'before' ? pair.beforeId : pair.afterId;
    const source = sources[side];
    const lit = (key: string, targetId: number | null, automatic: boolean): boolean =>
      targetId === selectedId &&
      (source?.kind === 'preset' ? source.key === key : !source && automatic);
    if (side === 'after') {
      const latest = items[items.length - 1]?.id ?? null;
      return [
        { key: 'latest', label: 'Latest', targetId: latest, active: lit('latest', latest, true) },
      ];
    }
    return COMPARE_PRESETS.map((preset) => {
      const targetId = resolvePreset(items, preset, pair.afterId)?.id ?? null;
      return {
        key: preset.key,
        label: preset.label,
        targetId,
        active: lit(preset.key, targetId, preset.key === 'first'),
      };
    });
  };

  const nearestLineFor = (side: CompareSide, item: PhotoIndexItem | undefined): string | null => {
    const source = sources[side];
    if (!item || !source) return null;
    const monthPreset =
      source.kind === 'preset' && source.key !== 'first' && source.key !== 'latest';
    return monthPreset || source.kind === 'date'
      ? `Nearest ${words.noun}: ${formatPhotoDay(item.entry_date)}`
      : null;
  };

  const viewer = (fullScreen: boolean): JSX.Element | null => {
    if (!beforeItem || !afterItem) return null;
    const before = sideImage(beforeItem, beforeShot.data, fullScreen);
    const after = sideImage(afterItem, afterShot.data, fullScreen);
    return (
      <CompareView
        mode={mode}
        // A new pair is a new slider: its photos' shape is measured again, and
        // the handle (reset to 50% above) starts from the middle.
        pairKey={`${beforeItem.id}-${afterItem.id}`}
        before={before}
        after={after}
        fullScreen={fullScreen}
        position={position}
        onPositionChange={setPosition}
        zoom={zoom}
        onZoomChange={setZoom}
        onImageError={onImageError}
        nounTitle={words.nounTitle}
      />
    );
  };

  const gap =
    beforeItem && afterItem ? (
      <Text
        size="sm"
        ta="center"
        style={{ fontVariantNumeric: 'tabular-nums' }}
        data-testid="compare-gap"
      >
        <Text span fw={600}>
          {gapLabel(beforeItem.entry_date, afterItem.entry_date)}
        </Text>
        <Text span c="gray.7">
          {' '}
          · {formatPhotoDay(beforeItem.entry_date)} to {formatPhotoDay(afterItem.entry_date)}
        </Text>
      </Text>
    ) : null;

  const modeControl = (
    <SegmentedControl
      value={mode}
      onChange={(value) => setMode(value as CompareMode)}
      data={COMPARE_MODES}
      size={isMobile ? 'md' : 'sm'}
      aria-label="Compare view"
    />
  );

  return (
    <Container size="xl" py={{ base: 'md', sm: 'xl' }} px={{ base: 'xs', sm: 'md' }}>
      <Stack gap="lg">
        <Group justify="space-between" wrap="nowrap" align="center">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {backMode === 'link' ? (
              <ActionIcon
                component={Link}
                to={backTo}
                variant="subtle"
                color="gray"
                size={isMobile ? 44 : 'lg'}
                aria-label={backLabel}
              >
                <IconArrowLeft size={16} />
              </ActionIcon>
            ) : (
              <ActionIcon
                variant="subtle"
                color="gray"
                size={isMobile ? 44 : 'lg'}
                aria-label={backLabel}
                onClick={goBack}
              >
                <IconArrowLeft size={16} />
              </ActionIcon>
            )}
            <Box visibleFrom="sm" style={{ display: 'flex', flexShrink: 0 }}>
              <IconLayoutColumns size={24} color="var(--mantine-color-teal-6)" />
            </Box>
            <div style={{ minWidth: 0 }}>
              <Title order={1} fz={{ base: 24, sm: 30 }} lh={1.2}>
                Compare
              </Title>
              <Text size="sm" c="dimmed" truncate>
                {subtitle}
              </Text>
            </div>
          </Group>
          {ready && (
            <Group gap="xs" wrap="nowrap">
              {api.shareTarget &&
                (isMobile ? (
                  <ActionIcon
                    variant="light"
                    size={44}
                    aria-label="Share"
                    onClick={() => setShareOpen(true)}
                  >
                    <IconShare size={16} />
                  </ActionIcon>
                ) : (
                  <Button
                    variant="light"
                    leftSection={<IconShare size={16} />}
                    onClick={() => setShareOpen(true)}
                  >
                    Share
                  </Button>
                ))}
              {isMobile ? (
                <ActionIcon
                  variant="light"
                  size={44}
                  aria-label="Full screen"
                  onClick={openFullScreen}
                >
                  <IconMaximize size={16} />
                </ActionIcon>
              ) : (
                <Button
                  variant="light"
                  leftSection={<IconMaximize size={16} />}
                  onClick={openFullScreen}
                >
                  Full screen
                </Button>
              )}
            </Group>
          )}
        </Group>

        {index.isError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title={words.loadError}>
            <Group gap="sm">
              <Text size="sm">{index.error.message}</Text>
              <Button
                size="compact-sm"
                variant="light"
                color="red"
                onClick={() => void refetchIndex()}
              >
                Try again
              </Button>
            </Group>
          </Alert>
        )}

        {loading && <Skeleton radius="md" h={isMobile ? 280 : 480} />}

        {notEnough && (
          <EmptyState
            icon={IconLayoutColumns}
            title={compareReason(items.length, words) ?? ''}
            description={emptyDescription}
            // Back the same way as the header's Back: by history on the organism
            // page, whose own Back is history too (no loop between the two).
            primaryAction={
              backMode === 'link'
                ? { label: backLabel, to: backTo }
                : { label: backLabel, onClick: goBack }
            }
          />
        )}

        {ready && pair && (
          <>
            <Stack gap="sm">
              <Stack gap={4} align="center">
                {modeControl}
                {/* Always mounted (empty when there is nothing to say), so the notice is announced. */}
                <Text size="sm" c="gray.7" ta="center" role="status" data-testid="compare-gone">
                  {goneNotice && (
                    <>
                      <IconInfoCircle size={16} className={classes.inlineIcon} aria-hidden />{' '}
                      {goneNotice}
                    </>
                  )}
                </Text>
              </Stack>
              {fullOpen ? (
                <Box h={heldHeight} aria-hidden data-testid="compare-viewer-held" />
              ) : (
                <Box ref={pageViewerRef}>{viewer(false)}</Box>
              )}
              {gap}
              {(beforeShot.isError || afterShot.isError) && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  <Group gap="sm">
                    <Text size="sm">A {words.noun} didn&apos;t load in full.</Text>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="red"
                      onClick={() => void refetchAll()}
                    >
                      Try again
                    </Button>
                  </Group>
                </Alert>
              )}
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: 'sm', md: 'md' }}>
              {(['before', 'after'] as const).map((side) => {
                const item = side === 'before' ? beforeItem : afterItem;
                const shotId = side === 'before' ? pair.beforeId : pair.afterId;
                const isReference = lineUpMode(shotId, reference) === 'reference';
                return (
                  <Stack key={side} gap="xs">
                    <CompareShotPicker
                      side={side}
                      items={items}
                      selectedId={side === 'before' ? pair.beforeId : pair.afterId}
                      otherId={side === 'before' ? pair.afterId : pair.beforeId}
                      chips={chipsFor(side)}
                      nearestLine={nearestLineFor(side, item)}
                      notice={notice?.side === side ? notice.text : null}
                      isMobile={isMobile}
                      onChip={(chip) => {
                        if (chip.targetId !== null) {
                          choose(side, chip.targetId, { kind: 'preset', key: chip.key });
                        }
                      }}
                      onPick={(id) => choose(side, id, { kind: 'pick' })}
                      onDate={(day) => chooseDate(side, day)}
                      onImageError={onImageError}
                      words={words}
                    />
                    {isReference ? (
                      <Text size="sm" c="dimmed" ta="center">
                        {words.referenceNote}
                      </Text>
                    ) : (
                      <Button
                        variant="light"
                        leftSection={<IconFocusCentered size={16} />}
                        h={isMobile ? 44 : undefined}
                        onClick={() => setLiningUp(side)}
                        disabled={!(side === 'before' ? beforeShot.data : afterShot.data)}
                      >
                        Line up {side === 'before' ? 'Before' : 'After'}
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </Stack>

      <CompareFullScreen
        opened={fullOpen && ready}
        onClose={() => setFullOpen(false)}
        caption={
          beforeItem && afterItem
            ? `${gapLabel(beforeItem.entry_date, afterItem.entry_date)} · ${formatPhotoDay(
                beforeItem.entry_date
              )} to ${formatPhotoDay(afterItem.entry_date)}`
            : null
        }
      >
        {viewer(true)}
      </CompareFullScreen>

      <PhotoLineUpModal
        shot={
          (liningUp === 'before'
            ? beforeShot.data
            : liningUp === 'after'
              ? afterShot.data
              : null) ?? null
        }
        reference={reference}
        words={words}
        onSave={api.saveFraming}
        onMakeReference={api.makeReference}
        onClose={() => setLiningUp(null)}
      />

      {ready && pair && beforeItem && afterItem && subject && api.shareTarget && (
        <ShareCompareModal
          opened={shareOpen}
          onClose={() => setShareOpen(false)}
          tankId={subject.tankId}
          tankName={shareName}
          pair={pair}
          beforeDate={beforeItem.entry_date}
          afterDate={afterItem.entry_date}
          target={api.shareTarget(pair)}
          organism={shareOrganism}
        />
      )}
    </Container>
  );
}
